import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ClockIcon } from '../../../../assets/icons/clock-icon.svg';
import { ReactComponent as CloseIcon } from '../../../../assets/icons/close-circle.svg';
import { ReactComponent as PaidAmountIcon } from '../../../../assets/icons/current-price-sae.svg';
import { ReactComponent as ProfileIcon } from '../../../../assets/icons/profile.svg';
import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  GuestChargingStationInterFace,
  RequestGuestChargingStationInterFace,
} from '../../../../interface/guest-charging-station-interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  disableGuestChargingStationFiltersApplied,
  fetchGuestChargingStation,
  fetchListGuestChargingStationFilters,
  updateGuestChargingStationFilters,
  setSortData,
} from '../../../../redux/slice/GuestChargingStationSlice';
import { setNotificationDetails } from '../../../../redux/slice/NotificationSlice';
import MyButton from '../../../../shared-components/button/Button';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import DisableSwitchWarning from '../../../../shared-components/disable-warning/DisableWarning';
import ModalBox from '../../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../../shared-components/success-notification/SuccessNotification';
import SwitchButton from '../../../../shared-components/switch-button-field/SwitchButton';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../../../shared-functions/ConvertUTCtoHHMM';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import { FormatUtcString } from '../../../../shared-functions/FormatUtcString';
import guestChargingApi from '../../../../utils/api/guest-charging';
import stationManagementApi from '../../../../utils/api/station-management';

import GuestChargingChart from './GuestChargingChart';

interface ModalBoxDetail {
  title: string | null;
  content: JSX.Element | null;
  notificationModal: boolean;
  header: boolean;
  closeModal: () => void;
}

/**
 * Guest charging component
 * @returns
 */
function GuestCharging() {
  const { t } = useTranslation('stationManagement');
  const dispatch = useAppDispatch();
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: null,
    notificationModal: false,
    header: false,
    /**
     * Close modal
     */
    closeModal: () => setModalBoxStatus('close'),
  });

  const {
    GuestChargingStation,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.guestChargingStation);
  const [selectedStation, setSelectedStation] = useState<GuestChargingStationInterFace | null>(
    null,
  );
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const { selectedChargingPoint } = useAppSelector((state) => state.stationManagement);
  /**
   * Handle the fetch the data in api function
   * @param data
   */
  const fetchData = (data: RequestGuestChargingStationInterFace) => {
    if (!loading && selectedChargingPoint) {
      dispatch(fetchGuestChargingStation({ ...data, chargingStation: selectedChargingPoint.id }));
    }
  };
  /**
   * Confirm the status change function
   * @param id
   * @param checked
   */
  const confirmStatusChange = (id: string, checked: boolean) => {
    guestChargingApi
      .updateGuestStatus(id, { isActive: checked })
      .then(() => {
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: '',
          content: (
            <SuccessNotification
              title={t('statusChangedTitle')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={() => {
                setModalBoxStatus('close');
                fetchData({ page: DefaultApiLimit.page, limit });
                // dispatch(updateSingleStation(response.data));
              }}
            />
          ),
          notificationModal: true,
          header: false,
          /**
           * Close modal with function to dispatch station details
           */
          closeModal: () => {
            setModalBoxStatus('close');
            // dispatch(updateSingleStation(response.data));
          },
        });
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Handles the availability status change for a device
   * Shows a confirmation modal when disabling a device
   * @param {AvailableDevice} device - The device to update
   * @param {boolean} checked - The new availability status
   */
  const handleAvailabilityChange = (device: GuestChargingStationInterFace, checked: boolean) => {
    if (checked) {
      setModalBoxStatus('open');
      setModalBoxDetail({
        title: t('disableWarningTitle'),
        content: (
          <DisableSwitchWarning
            selectedId={device._id}
            onCancel={() => setModalBoxStatus('close')}
            message={t('disableWarningMessage')}
            onConfirm={() => confirmStatusChange(device._id, checked)}
          />
        ),
        notificationModal: true,
        header: false,
        /**
         * Close modal
         */
        closeModal: () => setModalBoxStatus('close'),
      });
    } else {
      confirmStatusChange(device._id, checked);
    }
  };

  /**
   * Renders the custom data for the availability column
   * @param {AvailableDevice} device - The device data
   * @returns {JSX.Element} The rendered availability toggle cell
   */
  const renderAvailability = (device: GuestChargingStationInterFace) => (
    <div className="flex justify-center">
      <div key={device.id}>
        <SwitchButton
          checked={device.isActive}
          change={(checked: boolean) => handleAvailabilityChange(device, checked)}
        />
      </div>
    </div>
  );

  const column = [
    {
      label: t('consumerId'),
      accessorKey: 'userCustomId' as keyof GuestChargingStationInterFace,
      columnWidth: 200,

      /**
       * Side panel pages
       */
      customData: (consumer: GuestChargingStationInterFace) => (
        <button
          type="button"
          className="consumer-table-row"
          onClick={() => {
            setSelectedStation(consumer);
            setIsPanelOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              setSelectedStation(consumer);
              setIsPanelOpen(true);
            }
          }}>
          {consumer.userCustomId}
        </button>
      ),
    },
    {
      label: t('name'),
      accessorKey: 'name' as keyof GuestChargingStationInterFace,
      sortAllow: true,
    },
    {
      label: t('phoneNumber'),
      accessorKey: 'phoneNumber' as keyof GuestChargingStationInterFace,
    },
    {
      label: t('email'),
      accessorKey: 'email' as keyof GuestChargingStationInterFace,
    },
    {
      label: t('date'),
      accessorKey: 'fromDate' as keyof GuestChargingStationInterFace,
      sortAllow: true,
      /**
       * Date view
       * @param station
       * @param index
       */
      customData: (station: GuestChargingStationInterFace) => (
        <div className="table-row-each-data" style={{ alignSelf: 'center' }}>
          {ConvertUTCtoDate(station.fromDate)} - {ConvertUTCtoDate(station.toDate)}
        </div>
      ),
    },
    {
      label: t('time'),
      accessorKey: 'totalEnergyDispensed' as keyof GuestChargingStationInterFace,
      sortAllow: false,
      columnWidth: 160,
      /**
       * Date view
       * @param station
       * @param index
       */
      customData: (station: GuestChargingStationInterFace) => (
        <div className="table-row-each-data" style={{ alignSelf: 'center' }}>
          {FormatUtcString(ConvertUTCtoHHMM(station.createdAt))}
        </div>
      ),
    },
    {
      label: t('status'),
      accessorKey: 'uptimePercent' as keyof GuestChargingStationInterFace,
      customData: renderAvailability,
    },
    {
      label: t('duration'),
      accessorKey: 'duration' as keyof GuestChargingStationInterFace,
      sortAllow: true,
    },
  ];

  /**
   * Current columns for export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('consumerId'), accessorKey: 'userCustomId' },
    { label: t('name'), accessorKey: 'name' },
    { label: t('phoneNumber'), accessorKey: 'phoneNumber' },
    { label: t('date'), accessorKey: 'fromDate' },
    { label: t('time'), accessorKey: 'totalEnergyDispensed' },
    { label: t('status'), accessorKey: 'uptimePercent' },
    { label: t('duration'), accessorKey: 'duration' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    setExportLoader(true);
    const payload = {
      ...queryParameters,
      submissionStatus: 'approved',
      chargingStation: selectedChargingPoint?.id,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;

    stationManagementApi
      .fetchGuestChargingListExport(payload)
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: GuestChargingStationInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;
              case 'fromDate':
                return (
                  `${ConvertUTCtoDate(row?.createdAt)} - ${ConvertUTCtoDate(row?.toDate)}` || 'N/A'
                );
              case 'totalEnergyDispensed':
                return ConvertUTCtoHHMM(row?.createdAt) || 'N/A';
              case 'uptimePercent':
                return row?.isActive ? t('active') : t('inactive');
              default:
                return (row as any)[col?.accessorKey] || 'N/A';
            }
          }),
        );
        if (type === 'csv') {
          exportCSV(headers, rows, `${t('chargingStationApproved')}.csv`);
        } else {
          exportPDF(headers, rows, `${t('chargingStationApproved')}.pdf`);
        }
        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || t('somethingWentWrong'),
            },
          }),
        );
      });
  };

  /**
   * Function to handle data limit change
   */
  const handleLimitChange = (changeLimit: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: DefaultApiLimit.page,
      limit: changeLimit,
      chargingStation: selectedChargingPoint?.id,
      sortBy,
      sortField,
    });
  };
  /**
   * Function to handle page change
   * @param pageNumber
   */
  const handlePageChange = (pageNumber: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: pageNumber,
      limit,
      sortBy,
      sortField,
    });
  };
  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortBy, sortField });
  };

  /**
   * Function for sorting table data
   */
  const handleSortFunction = (sortData: { sortBy: 'asc' | 'desc'; sortField: string }) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  useEffect(() => {
    if (!selectedChargingPoint?.id) return;

    const currentId = selectedChargingPoint.id;
    const lastId = sessionStorage.getItem('GuestChargingStationFiltersList'); // string | null

    // If same id as last time -> do nothing
    if (lastId !== currentId || filters.length === 1) {
      dispatch(fetchListGuestChargingStationFilters({ chargingStation: currentId }));
    }
    const queryParameters = GetParametersFromCommonFilters(filters);

    if (GuestChargingStation.length === 0 || lastId !== currentId || isFilterUpdated) {
      fetchData({
        page,
        limit,
        ...queryParameters,
      });
      dispatch(disableGuestChargingStationFiltersApplied());
    }
    // Save this id so next time we can compare
    sessionStorage.setItem('GuestChargingStationFiltersList', currentId);
  }, [selectedChargingPoint?.id, filters]);

  return (
    <div className="guest-charging-station-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={modalBoxDetail.closeModal}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
      />
      <div className="guest-charging-left-content">
        <CustomTable
          version="v2"
          header={column}
          data={GuestChargingStation}
          serialNo
          currentPage={page}
          sizeChangeButtonRequired
          limit={limit}
          page={page}
          totalPages={totalPages}
          totalResults={totalResults}
          loading={loading}
          filterListLoading={filterListLoading}
          filters={filters}
          filtersToDispatch={updateGuestChargingStationFilters}
          filterListToDispatch={fetchListGuestChargingStationFilters}
          exportDataPdfOrCSV={exportDataPdfOrCSV}
          exportLoader={exportLoader}
          refreshCurrentPages={handleRefresh}
          handlePageChange={handlePageChange}
          handleLimitChange={handleLimitChange}
          topHeader={false}
          paginate
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          flexDirection
          onHiddenColumnsChange={setHiddenColumns}
        />
      </div>
      <div className="guest-charging-right-content">
        <p>{t('paidVsGuest')}</p>
        <GuestChargingChart />
      </div>{' '}
      {isPanelOpen && selectedStation && (
        <div className="side-panel-pages">
          <div className="close">
            <div>
              <MyButton
                label=""
                buttonType="none"
                svgIcon={<CloseIcon />}
                // disableHoverEffect={disableHover}
                onClickFunc={() => {
                  if (isPanelOpen) {
                    setIsPanelOpen(false);
                    setSelectedStation(null);
                  }
                }}
              />
            </div>
          </div>

          {/* Profile Section */}
          <div className="profile">
            {/* <img src={ProfileIcon} alt="profile" className="profile-image" /> */}
            <ProfileIcon />
            <div>
              <p className="name">{selectedStation.name}</p>
              <p className="gmail">{selectedStation.email}</p>
            </div>
          </div>

          {/* Details Section */}
          <div className="details">
            <div>
              <p className="label">{t('paymentMethod')}</p>
              <div className="detail-item">
                <p className="value">{selectedStation.paymentMethod}</p>
              </div>
            </div>
            <div>
              <p className="label">{t('phoneNumber')}</p>
              <div className="detail-item">
                <p className="value">{selectedStation.phoneNumber}</p>
              </div>
            </div>
            <div>
              <div className="label-with-icon">
                <PaidAmountIcon style={{ color: '#00A79D' }} />
                <p className="label">{t('totalPaidAmount')}</p>
              </div>
              <p className="value">{selectedStation?.transactionDetails?.totalPaidAmount || 0}</p>
            </div>
            <div>
              <div className="label-with-icon">
                <p className="label">{t('totalPowerConsumed')}</p>
              </div>
              <p className="value">
                {selectedStation?.transactionDetails?.totalPowerConsumed || 0}
              </p>
            </div>
          </div>

          {/* Session Section */}
          <div className="session-header">
            <ClockIcon style={{ color: '#00A79D' }} />
            <p className="session-title">{t('sessionDetails')}</p>
          </div>
          <div className="session-details">
            <div className="session-container">
              <p className="label">{t('startDateAndTime')}</p>
              <p className="value">
                {ConvertUTCtoDate(selectedStation.fromDate)}&{' '}
                {ConvertUTCtoHHMM(selectedStation.createdAt)}
              </p>
            </div>
            <div className="session-container">
              <p className="label">{t('endDateAndTime')}</p>
              <p className="value">
                {ConvertUTCtoDate(selectedStation.toDate)}&{' '}
                {ConvertUTCtoHHMM(selectedStation.createdAt)}
              </p>
            </div>
            <div className="session-container">
              <p className="label">{t('sessionStatus')}</p>
              <p className="value status">-</p>
            </div>
            <div className="session-container">
              <p className="label">{t('powerConsumed')}</p>
              <p className="value">-</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GuestCharging;
